import { Box, Flex, Icon, SimpleGrid, Text, VStack } from "@chakra-ui/react";
import { useSkills, useCategories } from "../hooks/useContent";
import { useLanguage } from "../context/LanguageContext";
import { skillCategories } from "../admin/collectionsConfig";
import { techIcon, techColor } from "../utils/techIcons";
import { ChipsSkeleton } from "./ui/Skeletons";
import Section from "./ui/Section";

function groupByCategory(skills) {
  const groups = {};
  const order = [];
  for (const skill of skills) {
    const cat = skill.category || "Other";
    if (!groups[cat]) {
      groups[cat] = [];
      order.push(cat);
    }
    groups[cat].push(skill);
  }
  // "Otros" (categoría other) siempre de última.
  const isOther = (c) => String(c || "").toLowerCase() === "other";
  const ordered = [...order.filter((c) => !isOther(c)), ...order.filter(isOther)];
  return ordered.map((cat) => ({ category: cat, skills: groups[cat] }));
}

function SkillTile({ skill }) {
  const IconComponent = techIcon(skill.icon);
  const color = techColor(skill.icon);
  return (
    <VStack spacing={3}>
      <Flex
        align="center"
        justify="center"
        boxSize={{ base: 16, md: 20 }}
        borderRadius="2xl"
        bg="white"
        border="1px solid"
        borderColor="border.card"
        boxShadow="sm"
        transition="transform 0.15s ease, box-shadow 0.15s ease"
        _hover={{ transform: "translateY(-4px)", boxShadow: "md" }}
      >
        {IconComponent ? (
          <Icon as={IconComponent} boxSize={{ base: 8, md: 10 }} color={color || "gray.700"} />
        ) : (
          <Text fontSize="xl" fontWeight="bold" color="gray.500">
            {skill.name.slice(0, 2)}
          </Text>
        )}
      </Flex>
      <Text fontSize="sm" color="text.muted" textAlign="center" noOfLines={1}>
        {skill.name}
      </Text>
    </VStack>
  );
}

export default function Skills() {
  const { language } = useLanguage();
  const { data: skills = [], loading } = useSkills();
  const { data: dbCats } = useCategories("skills");

  // Map a category slug to its localized label (DB categories, else static).
  const cats = dbCats.length ? dbCats : skillCategories;
  const catLabel = (slug) => {
    const c = cats.find((x) => x.value === slug);
    if (!c) return slug;
    return language === "es" ? c.label_es : c.label_en;
  };

  const groups = groupByCategory(skills);

  return (
    <Section
      id="skills"
      eyebrow={language === "es" ? "TECNOLOGÍAS" : "TECHNOLOGIES"}
      title={language === "es" ? "Tecnologías que uso" : "Technologies I use"}
    >
      {loading && (
        <>
          <ChipsSkeleton count={6} />
          <ChipsSkeleton count={5} />
        </>
      )}
      {!loading && groups.map(({ category, skills: catSkills }) => (
        <Box key={category} mb={10}>
          <Text
            fontSize="sm"
            fontWeight="bold"
            color="text.muted"
            letterSpacing="wider"
            mb={5}
            textTransform="uppercase"
          >
            {catLabel(category)}
          </Text>
          <SimpleGrid columns={{ base: 4, sm: 6, md: 10 }} spacing={{ base: 2, md: 2 }}>
            {catSkills.map((skill) => (
              <SkillTile key={skill.id ?? skill.name} skill={skill} />
            ))}
          </SimpleGrid>
        </Box>
      ))}
    </Section>
  );
}
